import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useState, useRef} from 'react';

import {
  loadChatId,
  loadUserMessage,
  saveStatusUrl,
  saveChatId,
  saveUserMessage,
  clearChatId,
  clearUserMessage,
  clearStatusUrl,
  loadStatusUrl,
} from 'llm/deep-research/DeepResearchChatStorage';
import {
  ProgressDataResponse,
  initiateDeepResearchMessage,
  pollDeepResearchMessage,
} from 'llm/deep-research/DeepResearchDAO';
import {
  DeepResearchMessageComponent,
  DeepResearchMessage,
} from 'llm/deep-research/messages/DeepResearchMessage';
import {
  ProgressDeepResearchMessage,
  ProgressData,
  ProgressStatus,
} from 'llm/deep-research/messages/DeepResearchProgress';
import {ReportDeepResearchMessage} from 'llm/deep-research/messages/DeepResearchReport';
import {DeepResearchMessageType} from 'llm/deep-research/types';
import {convertProgressDataResponseToProgressData} from 'llm/deep-research/utils';
import * as logging from 'util/Logging';

export interface DeepResearchMessageThreadControls {
  messages: Array<DeepResearchMessage>;
  progressData: ProgressData | null;
  chatId: string | undefined;
  isSubmitting: boolean;
  isDisabled: boolean;
  sendMessage: (messageContent: string) => Promise<void>;
  clearChat: () => void;
}

export function useDeepResearchMessageThread(): DeepResearchMessageThreadControls {
  // Create a ref to store the AbortController
  const abortControllerRef = useRef<AbortController>(new AbortController());
  // Initialize chatId from session storage
  const initialChatId = loadChatId();
  const initialStatusUrl = loadStatusUrl();

  const [messages, setMessages] = useState<Array<DeepResearchMessage>>([]);
  const [chatId, setChatId] = useState<string | undefined>(initialChatId);
  const [statusUrl, setStatusUrl] = useState<string | undefined>(initialStatusUrl);
  const [progressData, setProgressData] = useState<ProgressData | null>(null);

  // isDisabled controls whether the chat input is disabled. This is a temp variable to disable multi-turn
  // chats
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  interface UpdateProgressData {
    newProgressData?: ProgressData;
    status?: ProgressStatus;
    errorMessage?: string | null;
  }

  const updateProgressData = useCallback(({newProgressData, status, errorMessage}: UpdateProgressData) => {
    // Update progress data
    if (newProgressData) {
      setProgressData(newProgressData);
    }

    if (status) {
      // Update or add progress message
      setMessages((prevMessages) => {
        const progressMessageIndex = prevMessages.findLastIndex(
          (msg) => msg.type === DeepResearchMessageType.PROGRESS
        );

        const updatedMessages = [...prevMessages];
        const progressMessage: ProgressDeepResearchMessage = {
          type: DeepResearchMessageType.PROGRESS,
          status,
          errorMessage: errorMessage || undefined,
        };

        if (progressMessageIndex === -1) {
          // Add new progress message
          updatedMessages.push(progressMessage);
        } else {
          // Update existing progress message
          updatedMessages[progressMessageIndex] = progressMessage;
        }

        return updatedMessages;
      });
    }
  }, []);

  const updateProgressCallback = useCallback(
    (progressDataResponse: ProgressDataResponse) => {
      const newProgressData = convertProgressDataResponseToProgressData(progressDataResponse);
      updateProgressData({newProgressData, status: ProgressStatus.IN_PROGRESS});
    },
    [updateProgressData]
  );

  const addSystemReport = useCallback((content: string) => {
    const systemMessage: ReportDeepResearchMessage = {
      type: DeepResearchMessageType.SYSTEM_REPORT,
      content,
    };
    setMessages((prevMessages) => [...prevMessages, systemMessage]);
  }, []);

  // Poll for the result and set the messages
  useEffect(() => {
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    (async () => {
      if (chatId && statusUrl) {
        const abortController = abortControllerRef.current;
        // Set user message
        const userMessage: DeepResearchMessage = {
          type: DeepResearchMessageType.USER_MESSAGE,
          content: loadUserMessage() || '',
        };

        setMessages([userMessage]);
        updateProgressData({
          newProgressData: {numSources: 0, historyThread: []},
          status: ProgressStatus.STARTED,
        });

        setIsDisabled(true);
        setIsSubmitting(true);

        // Now poll for the result
        try {
          const result = await pollDeepResearchMessage(
            chatId,
            statusUrl,
            updateProgressCallback,
            abortController.signal
          );
          if (result.type === 'completed') {
            updateProgressData({status: ProgressStatus.COMPLETED});
            addSystemReport(result.response);
          } else {
            updateProgressData({status: ProgressStatus.FAILED, errorMessage: result.errorMessage});
          }
          setIsSubmitting(false);
        } catch (error) {
          // If the error is from an abort, we can safely ignore it
          if (error?.name !== 'AbortError') {
            throw error;
          }
        }
      } else {
        setMessages([]);
        setProgressData(null);
        setIsDisabled(false);
        setIsSubmitting(false);
      }
    })();
  }, [chatId, statusUrl, addSystemReport, updateProgressCallback, updateProgressData]);

  const sendMessage = useCallback(
    async (messageContent: string) => {
      const trimmedMessage = messageContent.trim();

      const userMessage: DeepResearchMessage = {
        type: DeepResearchMessageType.USER_MESSAGE,
        content: trimmedMessage,
      };

      setMessages((prevMessages) => [...prevMessages, userMessage]);
      setIsSubmitting(true);
      setIsDisabled(true);

      // First initiate the request to get the chatId immediately
      // TODO (BNCH-126624): Add try/catch for error handling
      const initResponse = await initiateDeepResearchMessage(chatId, trimmedMessage);

      // Store chatId as soon as we get it
      if (chatId && chatId !== initResponse.chatId) {
        logging.error('[llm] Chat ID mismatch', {
          extra: {storedChatId: chatId, responseChatId: initResponse.chatId},
        });
      }
      saveChatId(initResponse.chatId);
      saveStatusUrl(initResponse.statusUrl);
      saveUserMessage(trimmedMessage);

      // Set chat ID after all storage is updated
      setChatId(initResponse.chatId);
      setStatusUrl(initResponse.statusUrl);
    },
    [chatId, setIsSubmitting, setIsDisabled]
  );

  const clearChat = useCallback(() => {
    // Clear all storage items
    clearChatId();
    clearStatusUrl();
    clearUserMessage();

    // Reset states to allow new input
    setChatId(undefined);
    setStatusUrl(undefined);
  }, []);

  return {
    messages,
    progressData,
    chatId,
    isSubmitting,
    isDisabled,
    sendMessage,
    clearChat,
  };
}

export interface DeepResearchMessageThreadProps {
  messages: Array<DeepResearchMessage>;
  progressData: ProgressData | null;
  openActivityModal: (activityData: ProgressData, selectedToolId?: string) => void;
}

export function DeepResearchMessageThread({
  messages,
  progressData,
  openActivityModal,
}: DeepResearchMessageThreadProps): JSX.Element {
  return (
    <div className={css(styles.threadContainer)}>
      {messages.map((message, index) => (
        <DeepResearchMessageComponent
          key={index}
          message={message}
          progressData={progressData}
          openActivityModal={openActivityModal}
        />
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  threadContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    gap: '12px',
    height: '100%',
    width: '100%',
  },
});
